import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useEffect, useState } from "react";
import { FaChalkboard, FaMapMarkerAlt, FaVideo } from "react-icons/fa";
import { HiOutlineBookOpen, HiOutlineUser } from "react-icons/hi";
import {
  HiOutlineCalendar,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineMagnifyingGlass,
  HiOutlinePlus,
} from "react-icons/hi2";
import { Link } from "react-router-dom";
import Badge from "../../components/Badge";
import Button from "../../components/Button";
import Card from "../../components/Card";
import EmptyState from "../../components/EmptyState";
import LoadingSpinner from "../../components/LoadingSpinner";
import PageHeader from "../../components/PageHeader";
import StatCard from "../../components/StatCard";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../services/api";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

export default function StudentDashboard() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [sessionsRes, bookingsRes] = await Promise.all([
        api.get("/sessions?upcoming=true"),
        api.get("/bookings/user/my-bookings"),
      ]);
      setSessions(sessionsRes.sessions || []);
      setBookings(bookingsRes.bookings || []);

      // Calculate stats
      calculateStats(sessionsRes.sessions, bookingsRes.bookings);
    } catch (error) {
      console.error("Error loading dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (sessions, bookings) => {
    const upcomingCount = sessions.length;
    const totalBookings = bookings.length;
    const confirmedBookings = bookings.filter(
      (b) => b.status === "confirmed"
    ).length;
    const pendingBookings = bookings.filter(
      (b) =>
        b.status === "awaiting_tutor_confirmations" || b.status === "pending"
    ).length;

    setStats({
      upcoming: upcomingCount,
      total: totalBookings,
      confirmed: confirmedBookings,
      pending: pendingBookings,
    });
  };

  const getTimeUntil = (date) => {
    const now = dayjs();
    const target = dayjs(date);
    const hours = target.diff(now, "hour");

    if (hours < 1) {
      const minutes = target.diff(now, "minute");
      return `in ${minutes}m`;
    }
    if (hours < 24) {
      return `in ${hours}h`;
    }
    return target.fromNow();
  };

  if (loading) {
    return <LoadingSpinner size="lg" className="py-16" />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title={`Welcome back, ${user?.name?.first}!`}
        subtitle="Ready to learn something new today?"
        action={
          <Link to="/student/bookings/new">
            <Button variant="primary">
              <HiOutlinePlus className="w-5 h-5 mr-2" />
              Book a Session
            </Button>
          </Link>
        }
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Upcoming Sessions"
            value={stats.upcoming}
            icon={
              <HiOutlineCalendar className="w-8 h-8 text-white opacity-80" />
            }
            bgGradient="from-blue-500 to-blue-600"
            description="Scheduled sessions"
          />

          <StatCard
            title="Confirmed Sessions"
            value={stats.confirmed}
            icon={
              <HiOutlineCheckCircle className="w-8 h-8 text-white opacity-80" />
            }
            bgGradient="from-green-500 to-green-600"
            description="Confirmed bookings"
          />

          <StatCard
            title="Pending Sessions"
            value={stats.pending}
            icon={<HiOutlineClock className="w-8 h-8 text-white opacity-80" />}
            bgGradient="from-yellow-500 to-yellow-600"
            description="Awaiting confirmation"
          />

          <StatCard
            title="Total Sessions"
            value={stats.total}
            icon={
              <HiOutlineBookOpen className="w-8 h-8 text-white opacity-80" />
            }
            bgGradient="from-purple-500 to-purple-600"
            description="All time"
          />
        </div>
      )}

      {/* Quick Actions */}
      <Card className="mb-8">
        <h2 className="text-lg font-semibold text-text mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link
            to="/tutors"
            className="flex flex-col items-center p-4 rounded-lg hover:bg-surface/70 transition-colors"
          >
            <HiOutlineMagnifyingGlass className="w-8 h-8 text-primary mb-2" />
            <span className="text-sm font-medium text-text">Find Tutors</span>
          </Link>
          <Link
            to="/student/profile"
            className="flex flex-col items-center p-4 rounded-lg hover:bg-surface/70 transition-colors"
          >
            <HiOutlineUser className="w-8 h-8 text-primary mb-2" />
            <span className="text-sm font-medium text-text">My Profile</span>
          </Link>
          <Link
            to="/student/sessions"
            className="flex flex-col items-center p-4 rounded-lg hover:bg-surface/70 transition-colors"
          >
            <FaChalkboard className="w-8 h-8 text-primary mb-2" />
            <span className="text-sm font-medium text-text">All Sessions</span>
          </Link>
          <Link
            to="/student/bookings/new"
            className="flex flex-col items-center p-4 rounded-lg hover:bg-surface/70 transition-colors"
          >
            <HiOutlinePlus className="w-8 h-8 text-primary mb-2" />
            <span className="text-sm font-medium text-text">
              Book a Session
            </span>
          </Link>
        </div>
      </Card>

      {/* Main Content */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Upcoming Sessions */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-text">
              Upcoming Sessions
            </h2>
            {sessions.length > 0 && (
              <span className="bg-secondary/20 text-secondary dark:bg-secondary/30 text-xs font-semibold px-2.5 py-1 rounded-full">
                {sessions.length} session{sessions.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          {sessions.length === 0 ? (
            <EmptyState
              icon={HiOutlineCalendar}
              title="No upcoming sessions"
              description="Book a session to get started!"
              action={{ label: "Book Now", to: "/student/bookings/new" }}
            />
          ) : (
            <div className="space-y-4">
              {sessions.map((session) => (
                <Card key={session._id}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-lg text-text">
                        {session.subjectId?.name}
                      </h3>
                      <p className="text-sm text-text/70">
                        with {session.tutorId?.name?.first}{" "}
                        {session.tutorId?.name?.last}
                      </p>
                      <p className="text-xs text-text/60 mt-1 flex items-center gap-2">
                        {session.sessionType === "online" ? (
                          <>
                            <FaVideo className="flex-shrink-0 text-xs" /> Online
                          </>
                        ) : (
                          <>
                            <FaMapMarkerAlt className="flex-shrink-0 text-xs" />{" "}
                            In-Person
                          </>
                        )}
                        {session.sessionType === "in-person" &&
                          session.location &&
                          ` - ${session.location}`}
                      </p>
                    </div>
                    <Badge
                      label="Confirmed"
                      variant="success"
                      size="small"
                      className="capitalize font-medium"
                    />
                  </div>
                  <div className="flex items-center text-sm text-text/70 mb-4">
                    <HiOutlineCalendar className="w-4 h-4 mr-1" />
                    {dayjs
                      .utc(session.startAt)
                      .local()
                      .format("MMM D, YYYY [at] h:mm A")}
                    <span className="ml-2 text-primary font-medium">
                      ({getTimeUntil(session.startAt)})
                    </span>
                  </div>
                  <Link to={`/sessions/${session._id}`} className="block">
                    <Button className="w-full">View Details</Button>
                  </Link>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Recent Bookings */}
        <div>
          <h2 className="text-xl font-semibold mb-4 text-text">
            Recent Bookings
          </h2>
          {bookings.length === 0 ? (
            <EmptyState
              icon={HiOutlineBookOpen}
              title="No bookings yet"
              description="Start your learning journey today!"
              action={{ label: "Browse Tutors", to: "/tutors" }}
            />
          ) : (
            <div className="space-y-4">
              {bookings.slice(0, 5).map((booking) => {
                const badgeVariant =
                  booking.status === "confirmed"
                    ? "success"
                    : booking.status === "failed"
                    ? "error"
                    : booking.status === "cancelled"
                    ? "default"
                    : "warning";
                return (
                  <Card key={booking._id}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-lg text-text">
                          {booking.subjectId?.name}
                        </h3>
                        <p className="text-sm text-text/70">
                          {dayjs(booking.createdAt).format("MMM D, YYYY")}
                        </p>
                      </div>
                      <Badge
                        label={booking.status.replace(/_/g, " ")}
                        variant={badgeVariant}
                        size="small"
                        className="capitalize font-medium"
                      />
                    </div>
                    {booking.assignedTutorId && (
                      <p className="text-sm text-text/70 mb-4">
                        Tutor: {booking.assignedTutorId?.name?.first}{" "}
                        {booking.assignedTutorId?.name?.last}
                      </p>
                    )}
                    <Link to={`/bookings/${booking._id}`} className="block">
                      <Button variant="outline" className="w-full">
                        View Details
                      </Button>
                    </Link>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
